"use client";

import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { COUNTRIES, countryName, flagUrl, normalizeForSearch } from "@/lib/countries";
import { useLocale, useMessages } from "@/lib/i18n/client";
import messages from "./country-combobox.i18n";

interface CountryComboboxProps {
  value: string;
  onChange: (code: string) => void;
}

export function CountryCombobox({ value, onChange }: CountryComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const t = useMessages(messages);
  const lang = useLocale();

  // Nombres en el idioma de la página, ordenados en ese mismo idioma.
  const countries = useMemo(
    () =>
      COUNTRIES.map((c) => ({ code: c.code, name: countryName(c.code, lang) })).sort((a, b) =>
        a.name.localeCompare(b.name, lang),
      ),
    [lang],
  );

  const filtered = countries.filter((c) =>
    normalizeForSearch(c.name).includes(normalizeForSearch(search)),
  );

  function select(code: string) {
    onChange(code);
    setOpen(false);
    setSearch("");
  }

  const selected = countries.find((c) => c.code === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-7 justify-between gap-1.5 rounded-full px-2.5 text-xs font-normal"
        >
          <span className="flex items-center gap-1.5">
            {selected && (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={flagUrl(selected.code)} alt="" className="h-3 w-4 rounded-[2px] object-cover" />
            )}
            {selected?.name ?? t.choose}
          </span>
          <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder={t.search} value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandGroup>
              {filtered.map((c) => (
                <CommandItem key={c.code} value={c.code} onSelect={() => select(c.code)} className="text-sm">
                  <Check
                    className={cn("mr-2 h-4 w-4 shrink-0", value === c.code ? "opacity-100" : "opacity-0")}
                  />
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={flagUrl(c.code)} alt="" className="mr-2 h-3 w-4 rounded-[2px] object-cover" />
                  {c.name}
                </CommandItem>
              ))}
              {filtered.length === 0 && (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  {t.empty}
                </p>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
